import React from 'react';

/**
 * Format LLM messages array into readable UI components
 * Handles OpenAI-style message format with role, content, and optional name
 */
export const formatLLMMessages = (messages, options = {}) => {
  if (!Array.isArray(messages) || messages.length === 0) return null;

  const { showMetadata = true } = options;

  return (
    <div className="space-y-3">
      {messages.map((msg, idx) => (
        <div key={idx} className="border border-accent-lavender/20 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              msg.role === 'system' ? 'bg-accent-primary-base/20 text-accent-primary-base' :
              msg.role === 'user' ? 'bg-accent-amber/20 text-accent-amber' :
              msg.role === 'assistant' ? 'bg-accent-lavender/20 text-accent-lavender' :
              'bg-background-tertiary text-text-muted'
            }`}>
              {msg.role}
            </span>
            {msg.name && (
              <span className="text-xs text-text-muted">({msg.name})</span>
            )}
          </div>
          <div className="text-sm text-text-primary whitespace-pre-wrap break-words">
            {msg.content}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Format JSON content with proper indentation
 */
export const formatJSON = (content) => {
  try {
    const parsed = typeof content === 'string' ? JSON.parse(content) : content;
    return (
      <pre className="text-xs text-text-primary whitespace-pre-wrap break-words bg-background-primary p-3 rounded">
        {JSON.stringify(parsed, null, 2)}
      </pre>
    );
  } catch (e) {
    return null;
  }
};

/**
 * Format plain text with proper line breaks
 * Adds better styling for structured text with sections
 */
export const formatPlainText = (content) => {
  // Convert literal \n to actual newlines if needed
  const text = typeof content === 'string' && content.includes('\\n')
    ? content.replace(/\\n/g, '\n')
    : content;

  // Check if content has structure (multiple paragraphs or sections)
  const hasStructure = text.includes('\n\n') || text.includes('PREVIOUS CONVERSATION') || text.includes('IMPORTANT:');

  if (hasStructure) {
    return (
      <div className="text-sm text-text-primary whitespace-pre-wrap break-words bg-background-primary p-3 rounded max-h-96 overflow-y-auto">
        {text}
      </div>
    );
  }

  return (
    <div className="text-sm text-text-primary whitespace-pre-wrap break-words">
      {text}
    </div>
  );
};

/**
 * Auto-detect content type and format accordingly
 * Handles: LLM messages (JSON with messages array), plain JSON, plain text
 */
export const formatContent = (content) => {
  if (!content) return null;

  try {
    const parsed = typeof content === 'string' ? JSON.parse(content) : content;

    // Check if it's an LLM prompt format (has messages array)
    if (parsed.messages && Array.isArray(parsed.messages)) {
      return (
        <div>
          {formatLLMMessages(parsed.messages)}
          {(parsed.model || parsed.temperature !== undefined || parsed.stream !== undefined) && (
            <div className="text-xs text-text-muted space-y-1 mt-3 pt-3 border-t border-accent-lavender/20">
              {parsed.model && <div><span className="font-medium">Model:</span> {parsed.model}</div>}
              {parsed.temperature !== undefined && <div><span className="font-medium">Temperature:</span> {parsed.temperature}</div>}
              {parsed.stream !== undefined && <div><span className="font-medium">Stream:</span> {parsed.stream ? 'Yes' : 'No'}</div>}
            </div>
          )}
        </div>
      );
    }

    // Otherwise format as JSON
    return formatJSON(parsed);
  } catch (e) {
    // If not JSON, show as plain text
    return formatPlainText(content);
  }
};

/**
 * Format array of strings as pill badges
 */
export const formatBadgeList = (items, colorClass = 'bg-accent-lavender/20 text-accent-lavender') => {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <span key={idx} className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
          {item}
        </span>
      ))}
    </div>
  );
};

/**
 * Format categories as danger-colored badges
 */
export const formatCategories = (categories) => {
  return formatBadgeList(categories, 'bg-danger/20 text-danger');
};

/**
 * Format patterns as amber-colored badges
 */
export const formatPatterns = (patterns) => {
  return formatBadgeList(patterns, 'bg-accent-amber/20 text-accent-amber');
};
